import * as React from 'react';
import l10n from '../../util/l10n';
import globalEvents = require('../../util/globalEvents');
import Slider from 'material-ui/Slider';
import Warning from '../../components/Warning';
import CropOriginal from 'material-ui/svg-icons/notification/sync';
import IconTextButton from '../../custom-components/icon-text-button/IconTextButton';
import IieDefaultPayload from '../../image-inline-editor/core/DefaultPayload';
import {autoprefixer} from '../../material-ui/autoprefixer';
import {ImageModuleBaseData, ImageModuleData} from './interfaces';
import {BaseViewProps} from './interfaces';
import {getFitPayload} from '../../image-inline-editor/Utils';
import {getIieSourceImageWidth, getIieSourceImageHeight} from './util';

export default class ImageEditViewCrop extends React.Component<BaseViewProps, {}> {
    private zoomUsed = false;

    constructor(props: BaseViewProps) {
        super(props);
    }

    componentDidMount() {
        const newData = {} as ImageModuleBaseData;
        // allow crop and pan only in this view
        newData.allowCrop = true;
        newData.allowPan = true;
        this.props.onDataChange(newData);
    }

    componentWillUnmount() {
        const newData = {} as ImageModuleBaseData;
        // allow crop and pan only in this view
        newData.allowCrop = false;
        newData.allowPan = false;
        this.props.onDataChange(newData);
    }

    render() {
        const ZOOM_THRESHOLD_PERCENT = 15;
        const style = this.getStyle();
        const data = this.props.payload;
        const payload = Object.assign({}, IieDefaultPayload, data.iiePayload);

        let {minZoom, maxZoom} = this.getZoomLimits();
        let zoom = Number(payload.zoom.toFixed(4));
        if (zoom > maxZoom) {
            zoom = maxZoom;
        }
        if (zoom < minZoom) {
            zoom = minZoom;
        }
        const noZoom = minZoom >= maxZoom;
        const limitedZoom = (maxZoom - minZoom) / maxZoom < ZOOM_THRESHOLD_PERCENT / 100;
        if (noZoom) {
            maxZoom = zoom = minZoom;
        }

        return (
            <div>
                <div style={style.content}>
                    <div style={style.zoomLabel}>
                        {l10n('module.image.labels.zoom')}
                    </div>
                    <div style={style.zoomWrap}>
                        <Slider
                            // XXX Slider does not support minZoom and maxZoom changing
                            // so we generate key so that for each different
                            // min/max combination a new Slider element will be created
                            // see https://github.com/callemall/material-ui/issues/3619
                            key={`${minZoom}-${maxZoom}`}
                            onChange={this.onZoomChange}
                            onDragStart={this.onZoomStart}
                            onDragStop={this.onZoomStop}
                            disabled={noZoom || data.imageAnimated}
                            style={style.slider}
                            defaultValue={zoom}
                            value={zoom}
                            min={noZoom ? 0 : minZoom}
                            max={maxZoom}
                            step={0.0001}
                        />
                    </div>
                    <IconTextButton
                        text={l10n('module.image.buttons.resetCrop')}
                        Icon={<CropOriginal/>}
                        onTouchTap={this.onResetCrop}
                        isDisabled={noZoom || data.imageAnimated}
                        useAlternativeColors={true}
                    />
                </div>
                {!noZoom && limitedZoom ? <Warning text={l10n("module.image.messages.limitedZoom")}/> : null}
                {noZoom ? <Warning text={l10n("module.image.messages.noZoom")}/> : null}
            </div>
        );
    }

    private getZoomLimits() {
        return {
            minZoom: Number(this.getFitZoom().toFixed(4)),
            maxZoom: Number(this.getOriginalZoom().toFixed(4))
        };
    }

    private onResetCrop = () => {
        const data = this.props.payload;
        const sourceImageHeight = getIieSourceImageHeight(data);
        const sourceImageWidth = getIieSourceImageWidth(data);
        const aspectRatio = sourceImageWidth / sourceImageHeight;
        const newData = {} as ImageModuleBaseData;
        if ((data as ImageModuleData).isFullwidth) {
            // only make fit on width or height
            newData.iiePayload = getFitPayload(
                sourceImageWidth,
                sourceImageHeight,
                data.visualImageWidth,
                data.visualImageHeight
            );
        } else {
            newData.iiePayload = IieDefaultPayload;
            newData.visualImageHeight = data.visualImageHeight; // keep the height
            newData.visualImageWidth = newData.visualImageHeight * aspectRatio;

            const maxWidth = Math.min(sourceImageWidth, this.props.module.getMaxWidth());
            if (newData.visualImageWidth > maxWidth) {
                newData.visualImageWidth = maxWidth;
                newData.visualImageHeight = newData.visualImageWidth / aspectRatio;
            }
            newData.imageHeight = newData.visualImageHeight;
            newData.imageWidth = newData.visualImageWidth;
            newData.imageFit = newData.visualImageWidth >= this.props.module.getMaxWidth();
        }
        this.props.onDataChange(newData);
    };

    private onZoomChange = (e: React.MouseEvent<{}>, value: number) => {
        const payload = Object.assign({}, IieDefaultPayload, this.props.payload.iiePayload);
        const newData = {} as ImageModuleBaseData;
        newData.iiePayload = payload;
        newData.iiePayload.zoom = value;
        this.props.onDataChange(newData);
    };

    private onZoomStart = (e: React.MouseEvent<{}>) => {
        if (!this.zoomUsed) {
            this.zoomUsed = true;
            globalEvents.trigger('diy-editor-iie-zoom-used');
        }
        // XXX timeout to prevent a concurrent slider rerender
        setTimeout(() => {
            const newData = {} as ImageModuleBaseData;
            newData.showSourceImage = true;
            this.props.onDataChange(newData);
        }, 0);
    };

    private onZoomStop = (e: React.MouseEvent<{}>) => {
        const newData = {} as ImageModuleBaseData;
        newData.showSourceImage = false;
        this.props.onDataChange(newData);
    };

    private getFitZoom() {
        const data = this.props.payload;
        const cropWidth = data.visualImageWidth;
        const cropHeight = data.visualImageHeight;
        const sourceImageHeight = getIieSourceImageHeight(data);
        const sourceImageWidth = getIieSourceImageWidth(data);

        let fitZoom: number;
        let fitOnWidth = cropWidth / cropHeight > sourceImageWidth / sourceImageHeight;

        if (fitOnWidth) {
            fitZoom = 1;
        } else {
            let fitWidth = sourceImageWidth * cropHeight / sourceImageHeight;
            fitZoom = fitWidth / cropWidth;
        }
        return fitZoom;
    }

    private getOriginalZoom() {
        const data = this.props.payload;
        const zoom = getIieSourceImageWidth(data) / data.visualImageWidth;
        return zoom > 1 ? zoom : this.getFitZoom();
    }

    private getStyle() {
        return {
            content: autoprefixer({
                display: "flex"
            }),
            zoomLabel: autoprefixer({
                fontSize: 12,
                marginLeft: 12,
                marginRight: 12,
                alignSelf: "center",
            }),
            zoomWrap: autoprefixer({
                width: "60%",
                marginLeft: 12,
                marginRight: 24,
                alignSelf: "center"
            }),
            slider: {
                marginBottom: -48, // XXX reset the slider's built-in bottom margin
                marginTop: -24
            }
        };
    }
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/module/image/EditViewCrop.tsx
 **/